import type { PaymentOption } from 'src/models/cart/_types';
import type { Group, GroupMembership } from 'src/models/group/_types';
import type { User } from 'src/models/user/_types';

function showLifetimePreference(
  showLifetime: boolean | string | null | undefined
): boolean {
  return showLifetime === true || showLifetime === 'true';
}

/** Whether lifetime price should be shown on client-facing surfaces (checkout, landing). */
export function shouldShowLifetimePrice(group: {
  showLifetime?: boolean | string | null;
  oneTimePayment?: string | null;
  processorPlans?: string[] | null;
}): boolean {
  if (!group.oneTimePayment) {
    return false;
  }

  const hasPlans = (group.processorPlans?.length ?? 0) > 0;
  return showLifetimePreference(group.showLifetime) || !hasPlans;
}

/**
 * Filter group payment options for client checkout and purchase UI.
 * The API always includes the lifetime (purchase) option when configured;
 * hide it unless show_lifetime is enabled, only lifetime applies, or oneTime checkout.
 */
export function filterGroupPaymentOptions(
  group: Group | null | undefined,
  paymentOptions: PaymentOption[] | undefined,
  { oneTime }: { oneTime?: boolean } = {}
): PaymentOption[] {
  const options = paymentOptions ?? [];
  if (!group || options.length === 0) {
    return options;
  }

  if (oneTime) {
    return options.filter((option) => option.type === 'purchase');
  }

  const subscriptions = options.filter((option) => option.type === 'subscription');
  const onlyLifetime =
    subscriptions.length === 0 &&
    options.some((option) => option.type === 'purchase');

  if (showLifetimePreference(group.showLifetime) || onlyLifetime) {
    return options;
  }

  return options.filter((option) => option.type !== 'purchase');
}

interface IsGroupAdminArgs {
  user: User | null | undefined;
  group: Group | null | undefined;
}

export function isGroupAdmin({ user, group }: IsGroupAdminArgs): boolean {
  if (!user || !group) return false;
  const adminIds = Array.isArray(group.adminIds) ? group.adminIds : [];
  // user.id may be string/number; normalize to string for comparison
  const userId = String(user.id);
  return adminIds.map((id) => String(id)).includes(userId);
}

export function isGroupMember({
  group,
  memberships,
}: {
  group: Group;
  memberships: GroupMembership[] | null | undefined;
}): boolean {
  if (!group || !memberships || memberships.length === 0) return false;
  const gid = String(group.id);
  return memberships.some((m) => String(m.groupId) === gid);
}
